// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — the board, as DOM. 144 cells, built once and repainted in place.
//
// DOM RATHER THAN CANVAS, DELIBERATELY. This is a grid of discrete cells with text glyphs on it and
// no continuous motion that matters, so the DOM gives crisp type at 31px, real hit targets, and a
// layout that survives a phone rotating — for free, and without a devicePixelRatio dance. The cost
// is that a naive repaint of 144 nodes at 20Hz would thrash style recalculation, so every cell
// caches the exact class string it last wrote and touches the DOM only when that string changes.
// In a steady factory the vast majority of cells are unchanged on any given tick.
//
// HOW A PLAYER FINDS THE BOTTLENECK WITHOUT READING A NUMBER, IN FOUR LAYERS:
//   1. Item bunching plus a FILL BAR down the outgoing edge whenever a cell holds 2+ items. A
//      backed-up lane therefore draws one bright continuous line along its whole length and a
//      healthy one draws nothing. This is the dominant read, and it is the CONGESTION channel.
//   2. Rut colour, which is a different question — how much traffic this ground has carried. A
//      jammed lane is cool because it is carrying nothing, and that is correct rather than a bug;
//      conflating the two would make both unreadable. See the sim.ts header.
//   3. Machine marks as SHAPES, not hues: starved draws a ring with a gap, backed-up draws a filled
//      corner wedge, and RUNNING DRAWS NOTHING AT ALL. A healthy factory is undecorated, so the eye
//      finds problems by finding ornament rather than by scanning.
//   4. The strain overlay, which collapses both into the one number the player actually wants —
//      flow over break-even — on a single luminance ramp, and is the colour-blind escape hatch.

import { ITEM_GLYPH, Kind } from './modes';
import {
  N,
  SLOTS,
  State,
  W,
  breakEven,
  flowRate,
  machineState,
  tierOf,
} from './sim';

const KIND_CLASS: Record<number, string> = {
  [Kind.Empty]: 'e',
  [Kind.Boulder]: 'rock',
  [Kind.Belt]: 'belt',
  [Kind.Wye]: 'wye',
  [Kind.Digger]: 'dig',
  [Kind.Smelter]: 'sme',
  [Kind.Mill]: 'mil',
  [Kind.Jig]: 'jig',
  [Kind.Wharf]: 'wharf',
};

export interface View {
  root: HTMLElement;
  cellAt(clientX: number, clientY: number): number;
  paint(s: State, opts?: { strain?: boolean; ghost?: number[]; bad?: boolean }): void;
  flash(cells: number[]): void;
}

export function createBoard(host: HTMLElement): View {
  host.innerHTML = '';
  host.className = 'board';
  host.style.setProperty('--w', String(W));
  const cells: HTMLElement[] = [];
  const glyphHosts: HTMLElement[] = [];
  const last: string[] = new Array(N).fill('');
  const lastItems: string[] = new Array(N).fill('');

  for (let c = 0; c < N; c++) {
    const el = document.createElement('div');
    el.className = 'cell';
    el.dataset.c = String(c);
    const g = document.createElement('span');
    g.className = 'gl';
    el.appendChild(g);
    host.appendChild(el);
    cells.push(el);
    glyphHosts.push(g);
  }

  const cellAt = (clientX: number, clientY: number): number => {
    const r = host.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return -1;
    const x = Math.floor(((clientX - r.left) / r.width) * W);
    const y = Math.floor(((clientY - r.top) / r.height) * W);
    if (x < 0 || y < 0 || x >= W || y >= W) return -1;
    return y * W + x;
  };

  function paint(s: State, opts: { strain?: boolean; ghost?: number[]; bad?: boolean } = {}): void {
    const ghost = opts.ghost;
    for (let c = 0; c < N; c++) {
      const k = s.kind[c];
      const parts: string[] = ['cell', KIND_CLASS[k] ?? 'e'];

      if (opts.strain) {
        // One meaning, one ramp: how hard this cell is being pushed relative to what it carries for
        // free. Machines read as neutral so the lanes are the only thing speaking.
        const r = k === Kind.Belt || k === Kind.Wye ? flowRate(s, c) / breakEven(s.scour[c]) : 0;
        const step = r <= 0 ? 0 : r < 0.4 ? 1 : r < 0.7 ? 2 : r < 1.0 ? 3 : 4;
        parts.push('sx' + step);
      } else {
        parts.push('t' + tierOf(s.rut[c]));
        if (s.scour[c] > 0) parts.push('scoured');
      }

      if (s.patch[c]) parts.push(s.patch[c] === 1 ? 'shale' : 'loam');
      if (k === Kind.Belt || k === Kind.Wye) parts.push('d' + s.dir[c]);

      let n = 0;
      const base = c * SLOTS;
      for (let sl = 0; sl < SLOTS; sl++) if (s.slot[base + sl]) n++;
      // The congestion channel: 2+ items resident draws a bar along the outgoing edge, so a
      // backed-up run reads as one continuous bright line at a glance.
      if (n >= 2) parts.push('full');

      if (k === Kind.Smelter || k === Kind.Mill || k === Kind.Jig) {
        const st = machineState(s, c);
        if (st !== 'running') parts.push('st-' + st);
      }
      if (k === Kind.Digger && s.patch[c] && s.patchLeft[c] === 0 && s.mode === 'drought') parts.push('dry');

      if (ghost && ghost.includes(c)) parts.push(opts.bad ? 'ghost-bad' : 'ghost');

      const cls = parts.join(' ');
      if (cls !== last[c]) {
        cells[c].className = cls;
        last[c] = cls;
      }

      // items
      let txt = '';
      for (let sl = 0; sl < SLOTS; sl++) {
        const v = s.slot[base + sl];
        if (v) txt += ITEM_GLYPH[v - 1];
      }
      if (txt !== lastItems[c]) {
        glyphHosts[c].textContent = txt;
        lastItems[c] = txt;
      }
    }
  }

  function flash(list: number[]): void {
    for (const c of list) {
      if (c < 0 || c >= N) continue;
      cells[c].classList.add('flash');
      window.setTimeout(() => cells[c].classList.remove('flash'), 900);
    }
  }

  return { root: host, cellAt, paint, flash };
}

/**
 * The five cells carrying the most traffic, worst first. Drives the problem strip and the
 * end-of-run ground report from ONE function, so the two can never disagree.
 */
export function hottest(s: State, count = 5): number[] {
  const out: number[] = [];
  for (let c = 0; c < N; c++) if (s.kind[c] === Kind.Belt || s.kind[c] === Kind.Wye) out.push(c);
  out.sort((a, b) => s.rut[b] - s.rut[a] || a - b);
  return out.slice(0, count);
}
